/**
 * MobileNav — Bottom Tab Bar iOS/Android
 *
 * 5 onglets principaux : Tableau de Bord · Projets · Devis · IA · Réglages
 * Affiché uniquement sur mobile/natif. Sur desktop → Sidebar classique.
 *
 * Gère le safe-area bottom (home indicator iPhone X+)
 */
import { useDevice } from '../hooks/useDevice';

const GOLD   = '#C9A96E';
const INK    = '#1C1915';
const MUTED  = '#6B5F55';
const WHITE  = '#FFFFFF';

// Onglets mobiles — les 5 plus utiles sur le terrain
const MOBILE_TABS = [
  {
    id:    'dashboard',
    label: 'Bord',
    icon:  HomeIcon,
  },
  {
    id:    'projets',
    label: 'Projets',
    icon:  FolderIcon,
  },
  {
    id:    'devis-intelligent',
    label: 'Devis',
    icon:  DevisIcon,
  },
  {
    id:    'visualisation-temporelle-ai',
    label: 'IA',
    icon:  AIIcon,
  },
  {
    id:    '__settings',
    label: 'Réglages',
    icon:  SettingsIcon,
  },
];

export default function MobileNav({ activeTab, onTabChange, unsyncedCount = 0 }) {
  const { safeAreaBottom, isIOS } = useDevice();
  const bottomPad = Math.max(safeAreaBottom, isIOS ? 20 : 8);

  return (
    <nav style={{
      position:   'fixed',
      bottom:     0,
      left:       0,
      right:      0,
      zIndex:     1000,
      background: INK,
      borderTop:  '1px solid rgba(201,169,110,0.2)',
      paddingBottom: bottomPad,
      display:    'flex',
      boxShadow:  '0 -4px 20px rgba(0,0,0,0.25)',
    }}>
      {MOBILE_TABS.map(tab => {
        const isActive  = activeTab === tab.id ||
                          (tab.id === 'projets' && typeof activeTab === 'string' && activeTab.startsWith('projets:'));
        const Icon      = tab.icon;
        const showBadge = tab.id === 'devis-intelligent' && unsyncedCount > 0;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              flex:       1,
              display:    'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap:        3,
              padding:    '10px 4px 4px',
              background: 'none',
              border:     'none',
              cursor:     'pointer',
              position:   'relative',
              WebkitTapHighlightColor: 'transparent',
              outline:    'none',
              transition: 'opacity .1s',
              minHeight:  52,
            }}
          >
            {/* Indicator pill */}
            {isActive && (
              <div style={{
                position:  'absolute',
                top:        4,
                left:       '50%',
                transform: 'translateX(-50%)',
                width:     28,
                height:    3,
                borderRadius: 2,
                background: GOLD,
              }} />
            )}

            {/* Badge désynchro offline */}
            {showBadge && (
              <div style={{
                position:   'absolute',
                top:        6,
                right:      'calc(50% - 14px)',
                background: '#E57373',
                color:       WHITE,
                borderRadius: '50%',
                width:       16,
                height:      16,
                fontSize:    9,
                fontWeight:  800,
                display:    'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'system-ui',
              }}>
                {unsyncedCount > 9 ? '9+' : unsyncedCount}
              </div>
            )}

            <Icon
              size={22}
              color={isActive ? GOLD : MUTED}
              style={{ transition: 'color .15s' }}
            />
            <span style={{
              fontSize:    10,
              fontWeight:  isActive ? 700 : 500,
              color:       isActive ? GOLD : MUTED,
              fontFamily: "'DM Sans', system-ui, sans-serif",
              letterSpacing: '0.02em',
              transition: 'color .15s',
            }}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ── Icônes SVG inline ─────────────────────────────────────────────────────────
function HomeIcon({ size = 22, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}

function FolderIcon({ size = 22, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
    </svg>
  );
}

function DevisIcon({ size = 22, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2"/>
      <line x1="8" y1="7"  x2="16" y2="7"/>
      <line x1="8" y1="11" x2="16" y2="11"/>
      <line x1="8" y1="15" x2="12" y2="15"/>
    </svg>
  );
}

function AIIcon({ size = 22, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12"/>
    </svg>
  );
}

function SettingsIcon({ size = 22, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  );
}
